"use client"

export type GachaTracking = {
  referralCode: string | null
  campaignId: string | null
}

type StoredGachaTracking = GachaTracking & {
  createdAt: number
  expiresAt: number
}

const GACHA_TRACKING_STORAGE_KEY = "gacha:tracking"
const GACHA_TRACKING_TTL_MS = 90 * 24 * 60 * 60 * 1000
type GachaTrackingSearchParams = Pick<URLSearchParams, "get" | "has">

export function storeGachaTracking({
  referralCode,
  campaignId,
  now = Date.now(),
}: GachaTracking & { now?: number }) {
  if (typeof window === "undefined") return

  const normalizedTracking: StoredGachaTracking = {
    referralCode: normalizeTrackingValue(referralCode),
    campaignId: normalizeTrackingValue(campaignId),
    createdAt: now,
    expiresAt: now + GACHA_TRACKING_TTL_MS,
  }

  try {
    window.localStorage.setItem(
      GACHA_TRACKING_STORAGE_KEY,
      JSON.stringify(normalizedTracking),
    )
  } catch {
    // Tracking should never block the purchase flow.
  }
}

export function getStoredGachaTracking(now = Date.now()): GachaTracking {
  if (typeof window === "undefined") return emptyGachaTracking()

  try {
    const raw = window.localStorage.getItem(GACHA_TRACKING_STORAGE_KEY)
    if (!raw) return emptyGachaTracking()

    const parsed = JSON.parse(raw) as Partial<StoredGachaTracking>
    if (!parsed.expiresAt || parsed.expiresAt <= now) {
      window.localStorage.removeItem(GACHA_TRACKING_STORAGE_KEY)
      return emptyGachaTracking()
    }

    return {
      referralCode: normalizeTrackingValue(parsed.referralCode),
      campaignId: normalizeTrackingValue(parsed.campaignId),
    }
  } catch {
    return emptyGachaTracking()
  }
}

export function hasGachaTrackingParams(
  searchParams: GachaTrackingSearchParams,
) {
  return searchParams.has("ref") || searchParams.has("campaign")
}

export function getGachaTrackingFromSearchParams(
  searchParams: GachaTrackingSearchParams,
): GachaTracking {
  return {
    referralCode: normalizeTrackingValue(searchParams.get("ref")),
    campaignId: normalizeTrackingValue(searchParams.get("campaign")),
  }
}

function emptyGachaTracking(): GachaTracking {
  return { referralCode: null, campaignId: null }
}

function normalizeTrackingValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null
}
